import React, { useState } from "react";
import "./SaverCreditGrid.css";

// 2025 Saver's Credit AGI thresholds (from IRS Form 8880 — educational use)
const TABLE_CONFIG = {
  statuses: [
    { key: "married_joint", label: "Married filing jointly" },
    { key: "head", label: "Head of household" },
    { key: "single", label: "Single, MFS, QW" },
  ],
  rows: [
    {
      label: "50% credit rate",
      displayRate: "50%",
      ranges: {
        married_joint: { min: 0, max: 47500 },
        head: { min: 0, max: 35625 },
        single: { min: 0, max: 23750 },
      },
    },
    {
      label: "20% credit rate",
      displayRate: "20%",
      ranges: {
        married_joint: { min: 47501, max: 51000 },
        head: { min: 35626, max: 38250 },
        single: { min: 23751, max: 25500 },
      },
    },
    {
      label: "10% credit rate",
      displayRate: "10%",
      ranges: {
        married_joint: { min: 51001, max: 79000 },
        head: { min: 38251, max: 59250 },
        single: { min: 25501, max: 39500 },
      },
    },
    {
      label: "0% (above income limit)",
      displayRate: "0%",
      ranges: {
        married_joint: { min: 79001, max: Infinity },
        head: { min: 59251, max: Infinity },
        single: { min: 39501, max: Infinity },
      },
    },
  ],
};

// --------------------------------------------------
// Helpers
// --------------------------------------------------
function formatCurrency(value) {
  if (!Number.isFinite(value)) return "";
  return value.toLocaleString("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  });
}

function formatRange(range) {
  const { min, max } = range;

  if (!Number.isFinite(max)) {
    return `More than ${formatCurrency(min - 1)}`;
  }
  if (min === 0) {
    return `Up to ${formatCurrency(max)}`;
  }
  return `${formatCurrency(min)} – ${formatCurrency(max)}`;
}

function findActiveRow(rows, filingStatus, agi) {
  if (agi == null || Number.isNaN(agi) || !Number.isFinite(agi)) return null;

  for (let i = 0; i < rows.length; i++) {
    const range = rows[i].ranges[filingStatus];
    if (!range) continue;
    const { min, max } = range;
    if (agi >= min && agi <= max) return i;
  }
  return null;
}

// --------------------------------------------------
// Component
// --------------------------------------------------

const SaverCreditGrid = () => {
  const { statuses, rows } = TABLE_CONFIG;

  const [filingStatus, setFilingStatus] = useState("single");
  const [incomeInput, setIncomeInput] = useState("");

  const agi =
    incomeInput === "" ? null : Number(String(incomeInput).replace(/,/g, ""));

  const activeRowIndex = findActiveRow(rows, filingStatus, agi);
  const activeRow =
    activeRowIndex !== null && activeRowIndex !== undefined
      ? rows[activeRowIndex]
      : null;

  // Summary logic
  let summaryLine =
    "Enter an AGI and choose a filing status to see where it falls in the 2025 Saver's Credit table.";
  let summaryStateClass = "";
  let rateLabel = "";

  if (agi != null && activeRow) {
    rateLabel = activeRow.displayRate;

    if (activeRow.displayRate === "0%") {
      summaryLine =
        "In this 2025 grid, this AGI falls in the row where the Saver’s Credit Rate is 0% for that filing status.";
    } else {
      summaryLine = `In this 2025 grid, this AGI lines up with the ${activeRow.displayRate} Saver’s Credit Rate for that filing status.`;
    }

    summaryStateClass =
      activeRow.displayRate === "0%" ? "no-credit" : "has-credit";
  }

  return (
    <section className="sc-grid">
      <header className="sc-grid-header">
        <h2 className="sc-grid-title">
          2025 Saver&apos;s Credit — Income Range
        </h2>
      </header>

      {/* Controls */}
      <div className="sc-controls">
        <div className="sc-control">
          <label className="sc-label" htmlFor="filing-status">
            Filing status
          </label>
          <select
            id="filing-status"
            className="sc-select"
            value={filingStatus}
            onChange={(e) => setFilingStatus(e.target.value)}
          >
            {statuses.map((status) => (
              <option key={status.key} value={status.key}>
                {status.label}
              </option>
            ))}
          </select>
        </div>

        <div className="sc-control">
          <label className="sc-label" htmlFor="income">
            Adjusted gross income (AGI)
          </label>
          <input
            id="income"
            type="number"
            className="sc-input"
            placeholder="e.g., 32000"
            value={incomeInput}
            onChange={(e) => setIncomeInput(e.target.value)}
            min="0"
          />
        </div>
      </div>

      {/* Summary box */}
      <div className={`sc-grid-summary ${summaryStateClass}`.trim()}>
        {rateLabel && <div className="rate">{rateLabel}</div>}
        <div className="detail">{summaryLine}</div>
      </div>

      {/* Table */}
      <div className="sc-grid-table-wrapper">
        <table className="sc-table">
          <thead>
            <tr>
              <th className="sc-th sc-th-corner">Credit rate</th>

              {statuses.map((status) => (
                <th
                  key={status.key}
                  className={`sc-th sc-th-status ${
                    status.key === filingStatus ? "sc-col-active" : ""
                  }`}
                >
                  {status.label}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.map((row, rowIndex) => {
              const isActiveRow = rowIndex === activeRowIndex;

              return (
                <tr
                  key={row.label}
                  className={`sc-row ${isActiveRow ? "sc-row-active" : ""}`}
                >
                  <th className="sc-th sc-th-rate">
                    <span className="sc-rate-pill">{row.displayRate}</span>
                    <span className="sc-rate-caption">{row.label}</span>
                  </th>

                  {statuses.map((status) => {
                    const range = row.ranges[status.key];
                    const isActiveCol = status.key === filingStatus;
                    const isActiveCell = isActiveRow && isActiveCol;

                    const classes = [
                      "sc-td",
                      isActiveCol ? "sc-col-active" : "",
                      isActiveCell ? "sc-cell-active" : "",
                    ]
                      .filter(Boolean)
                      .join(" ");

                    return (
                      <td key={status.key} className={classes}>
                        <span className="sc-range">
                          {formatRange(range)}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {/* Single footnote */}
      <p className="sc-grid-footnote">
               
      </p>
    </section>
  );
};

export default SaverCreditGrid;
